import React, { FC, useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'

import {
  CODE_STATUS,
  codeResentMessage,
  COMMON_MIXPANEL_PROPERTIES,
  INVALID_CODE_STATUSES,
  ONE_TIME_PASSCODE_EVENTS,
  ONE_TIME_PASSCODE_LENGTH,
  PAGE_ROUTES,
  REGISTRATION_ENTRY_POINT_KEY,
  REGISTRATION_PAGES,
  useLyraIntl,
} from '@lyrahealth-inc/shared-app-logic'
import {
  OneTimePasscode,
  OneTimePasscodeProps,
} from '@lyrahealth-inc/ui-core-crossplatform/src/pages/oneTimePasscode/OneTimePasscode'

import { useTrackViewPage } from '../../../common/hooks/useTrackViewPage'
import { postGenerateOneTimePasscode, postOneTimePasscodeVerification } from '../../../common/http/data/verifyEmail'
import { handleOpenEmailPress } from '../../../common/utils/accountSetupUtils'
import { getDeviceUUID } from '../../../data/appGlobals/appGlobalsSelectors'
import { trackEventWithObj } from '../../../data/mixpanel'
import { getSelectedEssentialsTopicIds, getSelectedPathwayOption } from '../../../data/register/registerSelectors'
import { useAppDispatch } from '../../../data/storeConfiguration/store'
import { getWellnessCheckInStateResults } from '../../wellnessCheckIn/data/wellnessCheckInSelectors'

type RegistrationOneTimePasscodeContainer = {
  isPreview?: boolean
}

const RegistrationOneTimePasscodeContainer: FC<RegistrationOneTimePasscodeContainer> = ({ isPreview = false }) => {
  const navigate = useNavigate()
  const { activeLanguage } = useLyraIntl()
  const { formatMessage } = useIntl()
  const [searchParams] = useSearchParams()
  const dispatch = useAppDispatch()

  const [codeStatus, setCodeStatus] = useState<CODE_STATUS | undefined>(undefined)
  const [toastProps, setToastProps] = useState<OneTimePasscodeProps['toastProps'] | undefined>(undefined)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showErrorMessage, setShowErrorMessage] = useState(false)

  const deviceUUID = useSelector(getDeviceUUID)
  const selectedPathwayOption = useSelector(getSelectedPathwayOption)
  const selectedEssentialsTopicIds = useSelector(getSelectedEssentialsTopicIds)
  const wellnessCheckInStateResults = useSelector(getWellnessCheckInStateResults)
  const wellnessCheckInStorageStr = isPreview ? JSON.stringify(wellnessCheckInStateResults) : ''

  const email = searchParams.get('email') || ''
  const entryPointToTrackAfterRegistering = searchParams.get(REGISTRATION_ENTRY_POINT_KEY)

  useTrackViewPage(REGISTRATION_PAGES.ONE_TIME_PASSCODE_INPUT)

  useEffect(() => {
    if (!email) {
      navigate('/register/connect', { state: { isEmailError: true, isCodeRedirectError: true } })
    }
  }, [email, navigate])

  const sendOneTimePasscode = useCallback(async () => {
    const payload = {
      username: encodeURIComponent(email),
      language: encodeURIComponent(activeLanguage),
      ...(deviceUUID && { deviceUUID: encodeURIComponent(deviceUUID) }),
      ...(selectedPathwayOption && { pathwayOption: encodeURIComponent(selectedPathwayOption) }),
      ...(selectedEssentialsTopicIds && { essentialsTopicIds: encodeURIComponent(selectedEssentialsTopicIds) }),
      ...(entryPointToTrackAfterRegistering && { entryPoint: encodeURIComponent(entryPointToTrackAfterRegistering) }),
      ...(wellnessCheckInStorageStr && { wellnessCheckInBody: wellnessCheckInStorageStr }),
    }

    await postGenerateOneTimePasscode(payload)
  }, [
    email,
    activeLanguage,
    deviceUUID,
    selectedPathwayOption,
    selectedEssentialsTopicIds,
    entryPointToTrackAfterRegistering,
    wellnessCheckInStorageStr,
  ])

  const handleResendCode = async () => {
    setShowErrorMessage(false)

    try {
      await sendOneTimePasscode()
      setToastProps({
        text: formatMessage(codeResentMessage),
        toastType: 'success',
        id: 'OneTimePasscode-successToast',
        onAnimationEnd: () => setToastProps(undefined),
      })
      dispatch(
        trackEventWithObj({
          event: ONE_TIME_PASSCODE_EVENTS.RESEND_EMAIL_VERIFICATION_CODE,
          [COMMON_MIXPANEL_PROPERTIES.PAGE]: REGISTRATION_PAGES.ONE_TIME_PASSCODE_INPUT,
        }),
      )
    } catch (error) {
      console.error(`Unable to resend one time passcode - ${error}`)
      setShowErrorMessage(true)
    }
  }

  const handleSubmit = async ({ code }: { code: string }) => {
    setIsSubmitting(true)
    setShowErrorMessage(false)

    try {
      const payload = {
        username: encodeURIComponent(email),
        code: code,
        language: encodeURIComponent(activeLanguage),
        ...(deviceUUID && { deviceUUID: encodeURIComponent(deviceUUID) }),
        ...(selectedPathwayOption && { pathwayOption: encodeURIComponent(selectedPathwayOption) }),
        ...(selectedEssentialsTopicIds && { essentialsTopicIds: encodeURIComponent(selectedEssentialsTopicIds) }),
        ...(entryPointToTrackAfterRegistering && { entryPoint: encodeURIComponent(entryPointToTrackAfterRegistering) }),
        ...(wellnessCheckInStorageStr && { wellnessCheckInBody: wellnessCheckInStorageStr }),
      }

      const response = await postOneTimePasscodeVerification(payload)

      const { status, redirectUrl } = response?.data

      dispatch(
        trackEventWithObj({
          event: ONE_TIME_PASSCODE_EVENTS.EMAIL_VERIFICATION_CODE_INPUT,
          [COMMON_MIXPANEL_PROPERTIES.STATUS]: status,
          [COMMON_MIXPANEL_PROPERTIES.PAGE]: REGISTRATION_PAGES.ONE_TIME_PASSCODE_INPUT,
        }),
      )

      if (status === 'VALID' && redirectUrl) {
        navigate(redirectUrl)
      } else {
        setShowErrorMessage(true)
      }
    } catch (error: any) {
      const status = error?.response?.data?.status

      dispatch(
        trackEventWithObj({
          event: ONE_TIME_PASSCODE_EVENTS.EMAIL_VERIFICATION_CODE_INPUT,
          [COMMON_MIXPANEL_PROPERTIES.STATUS]: status,
          [COMMON_MIXPANEL_PROPERTIES.PAGE]: REGISTRATION_PAGES.ONE_TIME_PASSCODE_INPUT,
        }),
      )

      if (INVALID_CODE_STATUSES.has(status)) {
        setCodeStatus(CODE_STATUS.INVALID)
      } else if (status === 'EXPIRED') {
        setCodeStatus(CODE_STATUS.EXPIRED)
      } else {
        setShowErrorMessage(true)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return email ? (
    <OneTimePasscode
      email={email}
      length={ONE_TIME_PASSCODE_LENGTH}
      onContactUsPressed={() => {
        navigate(PAGE_ROUTES.CARE_TEAM_PHONE_NUMBERS)
      }}
      onOpenEmailPressed={handleOpenEmailPress}
      onResendCode={handleResendCode}
      onSubmit={handleSubmit}
      toastProps={toastProps}
      codeStatus={codeStatus}
      isSubmitting={isSubmitting}
      showErrorMessage={showErrorMessage}
      isPreview={isPreview}
    />
  ) : null
}

export default RegistrationOneTimePasscodeContainer
